import { usesOrbitRoles } from "@/lib/roles";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Mail, Lock, BarChart3, ClipboardList, MapPin, Rocket, Globe, ChevronDown, Building2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { InstallBanner } from "@/components/InstallBanner";

const LOGO_FULL_COLOR = "/logos/orbit-full-trimmed.png";

export default function Login() {
  const { login, error, setError } = useAuth();
  const nav = useNavigate();
  const [tenant, setTenant] = useState("");
  const [tenantOk, setTenantOk] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const checkTenant = async () => {
    const slug = tenant.trim().toLowerCase();
    if (!slug) { setTenantOk(null); return true; }
    try { const { data } = await api.get("/tenants/check", { params: { slug } }); setTenantOk(data); setError(""); return true; }
    catch (e) {
      const st = e.response?.status;
      if (st === 404) { setTenantOk(false); setError("Espace introuvable. Vérifiez votre code entreprise."); return false; }
      setTenantOk(null);
      setError(st ? `Service temporairement indisponible (${e.response?.data?.detail?.code || st}). Votre code entreprise n'est pas en cause : réessayez dans un instant.` : "Serveur injoignable ou trop lent (délai dépassé). Votre code entreprise n'est pas en cause : réessayez dans un instant.");
      return false;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await checkTenant();
    if (!ok) { setLoading(false); return; }
    const u = await login(email, password, tenant);
    setLoading(false);
    if (u?.must_change_password) { toast.info("Mot de passe temporaire : merci de définir un nouveau mot de passe."); nav("/profile", { replace: true }); return; }
    if (u) nav(u.is_corner && !usesOrbitRoles(u.tenant) ? "/kpi-corner" : "/", { replace: true });
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-[#0a0a1f] overflow-hidden px-6 py-10" data-testid="login-page">
      {/* ═══════════════════════ Fond décoratif (starfield + orbites) ═══════════════════════ */}
      <div className="absolute inset-0 pointer-events-none text-white overflow-hidden"
           style={{
             background: "radial-gradient(circle at 50% 60%, #1e1b4b 0%, #0a0a1f 55%, #050514 100%)",
           }}>
        {/* Starfield */}
        <div className="absolute inset-0 opacity-70">
          {[...Array(60)].map((_, i) => {
            const size = Math.random() * 2 + 0.5;
            return (
              <div key={i}
                   className="absolute rounded-full bg-white"
                   style={{
                     left: `${Math.random() * 100}%`,
                     top: `${Math.random() * 100}%`,
                     width: `${size}px`,
                     height: `${size}px`,
                     opacity: Math.random() * 0.7 + 0.2,
                   }}/>
            );
          })}
        </div>
        {/* Planet glow bottom */}
        <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(99,102,241,0.25) 0%, rgba(30,27,75,0.15) 40%, transparent 70%)" }}/>
        {/* Planet curved ring bottom */}
        <div className="absolute -bottom-96 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full border border-indigo-500/30 pointer-events-none"/>
        <div className="absolute -bottom-80 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] rounded-full border border-indigo-500/20 pointer-events-none"/>

      </div>

      {/* ═══════════════════════ Composition centrée : slogan (haut-gauche) · carte connexion · KPI (bas-droite) ═══════════════════════ */}
      <div className="relative z-10 w-full max-w-[1180px] grid lg:grid-cols-[minmax(0,1fr)_480px_minmax(0,1fr)] gap-10 items-stretch">
        {/* Slogan — aligné en haut à gauche de la carte */}
        <div className="hidden lg:flex flex-col justify-start pt-4 text-white" data-testid="login-slogan">
          <div className="inline-flex w-fit items-center gap-2 px-3 py-1 rounded-full border border-indigo-400/30 bg-indigo-500/10 text-[10px] tracking-[0.2em] text-indigo-200 uppercase font-semibold mb-5">
            Plateforme de pilotage terrain
          </div>
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">
            Toute votre activité<br/>
            terrain, <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">en orbite.</span>
          </h1>
          <p className="mt-5 text-slate-300 text-sm leading-relaxed max-w-sm">
            Centralisez visites, problématiques et plans d'action. Donnez à vos directeurs régionaux une vision claire, en temps réel.
          </p>
        </div>

        {/* Carte de connexion — centrée */}
        <div className="flex flex-col justify-center relative w-full bg-white rounded-[28px] shadow-2xl shadow-black/40 px-8 sm:px-12 py-10 mx-auto max-w-[480px]" data-testid="login-card">
        <button className="absolute top-6 right-6 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 transition-colors"
                data-testid="login-lang" onClick={() => toast.info("Sélection de langue à venir")}>
          <Globe className="w-3.5 h-3.5"/>
          <span className="font-semibold">Français</span>
          <ChevronDown className="w-3 h-3"/>
        </button>

        {/* Logo Orbit — unique sur la page */}
        <div className="mx-auto mb-6">
          <img src={LOGO_FULL_COLOR} alt="Orbit — Operational Reporting & BI Tracker" className="h-20 w-auto object-contain" data-testid="login-logo"/>
        </div>

        <div className="text-center">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Bon retour parmi nous&nbsp;!</h2>
          <p className="text-slate-500 text-sm mt-2">Connectez-vous pour piloter votre secteur.</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
          <div>
            <label className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Code entreprise</label>
            <div className="mt-1.5 relative">
              <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input
                data-testid="login-tenant"
                type="text" autoFocus autoCapitalize="none" autoCorrect="off" spellCheck={false}
                value={tenant} onChange={(e) => { setTenant(e.target.value); setTenantOk(null); }} onBlur={checkTenant}
                className={`w-full pl-10 pr-10 py-3 rounded-xl border bg-slate-50/50 outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 focus:bg-white text-sm transition-colors ${tenantOk === false ? "border-rose-300" : tenantOk ? "border-emerald-300" : "border-slate-200"}`}
                placeholder="Code de votre espace (ex : demo)"
              />
              {tenantOk && <CheckCircle2 data-testid="login-tenant-ok" className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500"/>}
            </div>
            {tenantOk && <div data-testid="login-tenant-name" className="mt-1 text-[11px] text-emerald-600 font-medium">Espace {tenantOk.name}</div>}
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Email</label>
            <div className="mt-1.5 relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input
                data-testid="login-email"
                type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 focus:bg-white text-sm transition-colors"
                placeholder="vous@orbit.fr"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Mot de passe</label>
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-indigo-600 hover:underline font-medium">
                Mot de passe oublié&nbsp;?
              </Link>
            </div>
            <div className="mt-1.5 relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input
                data-testid="login-password"
                type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 focus:bg-white text-sm transition-colors"
                placeholder="••••••••"
              />
            </div>
          </div>
          {error && (
            <div data-testid="login-error"
                 className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-xl px-4 py-3">
              {error}
            </div>
          )}
          <button
            data-testid="login-submit"
            type="submit" disabled={loading}
            className="w-full py-3 rounded-xl text-white font-semibold shadow-lg shadow-indigo-500/30 transition-all disabled:opacity-60 hover:shadow-xl hover:shadow-indigo-500/40 active:scale-[0.99]"
            style={{ background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)" }}>
            {loading ? "Connexion…" : "Se connecter"}
          </button>

          {/* Divider */}
          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"/>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs text-slate-400 font-medium">ou</span>
            </div>
          </div>

          {/* SSO */}
          <button
            type="button"
            data-testid="login-sso"
            onClick={() => toast.info("SSO à configurer par votre administrateur")}
            className="w-full py-3 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 text-slate-700 font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors">
            <CheckCircle2 className="w-4 h-4 text-indigo-500"/>
            Se connecter avec SSO
          </button>
        </form>

        <InstallBanner/>

        {/* Bottom callout */}
        <div className="mt-8 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100/60 p-4 flex gap-3">
          <div className="w-9 h-9 rounded-xl bg-white grid place-items-center shrink-0 shadow-sm">
            <Rocket className="w-4 h-4 text-indigo-600"/>
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm text-slate-900">Performance. Réactivité. Impact.</div>
            <div className="text-xs text-slate-600 leading-snug mt-0.5">
              Orbit vous donne les clés pour agir vite,<br/>au bon endroit, avec les bonnes données.
            </div>
          </div>
        </div>
      </div>
        {/* 3 carrés KPI — alignés en bas à droite de la carte */}
        <div className="hidden lg:flex flex-col justify-end pb-4" data-testid="login-kpis">
          <div className="grid grid-cols-3 gap-3 max-w-md ml-auto">
            {[
              { icon: MapPin,         label: "Visites cette semaine", value: "+42",  hint: "vs semaine précédente", tint: "text-indigo-300" },
              { icon: ClipboardList,  label: "Plans d'action actifs", value: "23",   hint: "dont 8 prioritaires",   tint: "text-purple-300", trend: "↑" },
              { icon: BarChart3,      label: "Résolutions",           value: "91%",  hint: "taux de résolution",    tint: "text-emerald-300" },
            ].map((k, i) => (
              <div key={i} className="aspect-square rounded-2xl p-4 bg-white/[0.04] border border-white/10 backdrop-blur-sm text-white flex flex-col">
                <k.icon className={`w-4 h-4 ${k.tint} mb-auto`}/>
                <div className="text-[9px] tracking-[0.15em] text-slate-400 uppercase font-semibold leading-tight">{k.label}</div>
                <div className="font-display font-bold text-2xl mt-1 flex items-baseline gap-1">{k.value}{k.trend && <span className="text-base text-emerald-400">{k.trend}</span>}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{k.hint}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
